import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import '../assets/home.css';
import '../assets/home-rwd.css';
import '../assets/homev2.css';
import '../assets/homev3.css';

const HomeV4 = () => {
    const canvasRef = useRef(null);
    const sceneRef = useRef({
        scene: null,
        camera: null,
        renderer: null,
        controls: null,
        stemCell: null,
        animationId: null
    });

    // Initialize the 3D scene
    useEffect(() => {
        if (!canvasRef.current) return;

        // Setup scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xffffff); // White background as requested

        // Setup camera
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 0, 5);

        // Setup renderer
        const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.shadowMap.enabled = true;

        // Setup orbit controls for camera movement (enables rotation)
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.minDistance = 3;
        controls.maxDistance = 20;

        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(5, 5, 5);
        directionalLight.castShadow = true;
        scene.add(directionalLight);

        // Store references
        sceneRef.current = {
            scene,
            camera,
            renderer,
            controls
        };

        // Create stem cell
        createStemCell(scene);

        // Handle window resize
        const handleResize = () => {
            const { camera, renderer } = sceneRef.current;
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        // Animation loop
        const animate = () => {
            const animationId = requestAnimationFrame(animate);
            sceneRef.current.animationId = animationId;
            
            // Rotate the stem cell slightly every frame
            if (sceneRef.current.stemCell) {
                sceneRef.current.stemCell.rotation.y += 0.003;
                sceneRef.current.stemCell.rotation.x += 0.001;
            }
            
            sceneRef.current.controls.update();
            sceneRef.current.renderer.render(sceneRef.current.scene, sceneRef.current.camera);
        };

        animate();

        return () => {
            window.removeEventListener('resize', handleResize);
            
            // Cancel animation frame
            if (sceneRef.current.animationId) {
                cancelAnimationFrame(sceneRef.current.animationId);
            }
            
            // Clean up resources
            renderer.dispose();
            
            // Remove all objects from the scene
            while(scene.children.length > 0) { 
                const object = scene.children[0];
                scene.remove(object);
            }
        };
    }, []);

    // Function to create the stem cell
    const createStemCell = (scene) => {
        // Create a group to hold all stem cell components
        const stemCellGroup = new THREE.Group();
        
        // Create the main cell body (nucleus)
        const nucleusGeometry = new THREE.SphereGeometry(1.2, 32, 32);
        const nucleusMaterial = new THREE.MeshPhongMaterial({
            color: 0x4287f5, // Blue color
            transparent: true,
            opacity: 0.8,
            shininess: 100
        });
        const nucleus = new THREE.Mesh(nucleusGeometry, nucleusMaterial);
        nucleus.castShadow = true;
        stemCellGroup.add(nucleus);
        
        // Create the cell membrane (outer layer)
        const membraneGeometry = new THREE.SphereGeometry(1.4, 32, 32);
        const membraneMaterial = new THREE.MeshPhongMaterial({
            color: 0x91cdff, // Light blue
            transparent: true,
            opacity: 0.4,
            shininess: 80,
            side: THREE.DoubleSide
        });
        const membrane = new THREE.Mesh(membraneGeometry, membraneMaterial);
        membrane.castShadow = true;
        stemCellGroup.add(membrane);
        
        // Add organelles within the cell
        addOrganelles(stemCellGroup);
        
        // Add extensions/microvilli to the cell surface
        addCellExtensions(stemCellGroup);
        
        // Add the stem cell group to the scene
        scene.add(stemCellGroup);
        
        // Store reference to stem cell
        sceneRef.current.stemCell = stemCellGroup;
    };
    
    // Function to add organelles to the stem cell
    const addOrganelles = (stemCellGroup) => {
        // Add mitochondria (several small ellipsoids)
        for (let i = 0; i < 12; i++) {
            const size = 0.1 + Math.random() * 0.15;
            const mitochondriaGeometry = new THREE.SphereGeometry(size, 16, 16);
            const mitochondriaMaterial = new THREE.MeshPhongMaterial({
                color: 0xff5959, // Red
                shininess: 80
            });
            const mitochondria = new THREE.Mesh(mitochondriaGeometry, mitochondriaMaterial);
            
            // Position randomly within the cell
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            const radius = 0.3 + Math.random() * 0.7; // Keep inside the nucleus
            
            mitochondria.position.x = radius * Math.sin(phi) * Math.cos(theta);
            mitochondria.position.y = radius * Math.sin(phi) * Math.sin(theta);
            mitochondria.position.z = radius * Math.cos(phi);
            
            mitochondria.castShadow = true;
            stemCellGroup.add(mitochondria);
        }
        
        // Add Golgi apparatus (stack of curved discs)
        const golgiGroup = new THREE.Group();
        for (let i = 0; i < 5; i++) {
            const golgiGeometry = new THREE.TorusGeometry(0.2, 0.05, 16, 32, Math.PI);
            const golgiMaterial = new THREE.MeshPhongMaterial({
                color: 0x77dd77, // Green
                shininess: 70
            });
            const golgi = new THREE.Mesh(golgiGeometry, golgiMaterial);
            golgi.position.y = i * 0.08;
            golgi.rotation.x = Math.PI / 2;
            golgi.castShadow = true;
            golgiGroup.add(golgi);
        }
        golgiGroup.position.set(0.5, 0.2, 0);
        stemCellGroup.add(golgiGroup);
        
        // Add endoplasmic reticulum (network of tubes)
        const erGroup = new THREE.Group();
        for (let i = 0; i < 15; i++) {
            const tubeRadius = 0.02 + Math.random() * 0.02;
            const tubeLength = 0.1 + Math.random() * 0.3;
            const erGeometry = new THREE.CylinderGeometry(tubeRadius, tubeRadius, tubeLength, 8);
            const erMaterial = new THREE.MeshPhongMaterial({
                color: 0xfdfd96, // Yellow
                shininess: 60
            });
            const erTube = new THREE.Mesh(erGeometry, erMaterial);
            
            // Random position and rotation
            erTube.position.set(
                -0.6 + Math.random() * 0.4,
                -0.2 + Math.random() * 0.4,
                -0.2 + Math.random() * 0.4
            );
            erTube.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            
            erTube.castShadow = true;
            erGroup.add(erTube);
        }
        stemCellGroup.add(erGroup);
        
        // Add cell nucleus (DNA-containing center)
        const nucleolusGeometry = new THREE.SphereGeometry(0.5, 24, 24);
        const nucleolusMaterial = new THREE.MeshPhongMaterial({
            color: 0x7085d6, // Purple-blue
            shininess: 90
        });
        const nucleolus = new THREE.Mesh(nucleolusGeometry, nucleolusMaterial);
        nucleolus.castShadow = true;
        stemCellGroup.add(nucleolus);
        
        // Add chromatin (DNA material) as small dots within the nucleus
        for (let i = 0; i < 20; i++) {
            const chromatinGeometry = new THREE.SphereGeometry(0.03 + Math.random() * 0.03, 8, 8);
            const chromatinMaterial = new THREE.MeshPhongMaterial({
                color: 0x0033aa, // Dark blue
                shininess: 80
            });
            const chromatin = new THREE.Mesh(chromatinGeometry, chromatinMaterial);
            
            // Position within the nucleolus
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            const radius = Math.random() * 0.4;
            
            chromatin.position.x = radius * Math.sin(phi) * Math.cos(theta);
            chromatin.position.y = radius * Math.sin(phi) * Math.sin(theta);
            chromatin.position.z = radius * Math.cos(phi);
            
            chromatin.castShadow = true;
            nucleolus.add(chromatin);
        }
    };
    
    // Function to add microvilli (cell surface projections)
    const addCellExtensions = (stemCellGroup) => {
        for (let i = 0; i < 60; i++) {
            const height = 0.1 + Math.random() * 0.3;
            const extensionGeometry = new THREE.ConeGeometry(0.05, height, 8);
            const extensionMaterial = new THREE.MeshPhongMaterial({
                color: 0x91cdff, // Light blue
                transparent: true,
                opacity: 0.6,
                shininess: 70
            });
            const extension = new THREE.Mesh(extensionGeometry, extensionMaterial);
            
            // Position on the surface of the cell
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            const radius = 1.4; // Positioned at the membrane
            
            extension.position.x = radius * Math.sin(phi) * Math.cos(theta);
            extension.position.y = radius * Math.sin(phi) * Math.sin(theta);
            extension.position.z = radius * Math.cos(phi);
            
            // Orient the extension to point outward
            extension.lookAt(extension.position.clone().multiplyScalar(2));
            
            extension.castShadow = true;
            stemCellGroup.add(extension);
        }
    };

    return (
        <div className="home-container">
            <div className="canvas-container">
                <canvas ref={canvasRef} className="main-canvas"></canvas>
            </div>
            <div className="home-title">
                <h1>3D Stem Cell Visualization</h1>
                <p>Interactive 3D model of a stem cell. Use mouse to rotate and zoom.</p>
            </div>
        </div>
    );
};

export default HomeV4; 